import React from "react";
import { Badge, CarouselItem } from "../ui";
import { Avatar, AvatarImage, AvatarFallback } from "../ui/Avatar";
import { Student } from "@/types/Student";
import { StudentPopover } from "../StudentPopover";
import { WithLocationStudentCard } from "./WithLocationStudentCard";
import { WithMajorStudentCard } from "./WithMajorStudentCard";
import { useProtectedNavigation } from "@/routes/useProtectedNavigation";
import {
  FunnelStateEnumToGroup,
  translateGroupName,
} from "@/util/funnelGroups";

interface StudentCardProps {
  student: Partial<Student>;
  idx: number;
  showLocation?: boolean;
}

export const StudentCard = ({
  student,
  idx,
  showLocation = false,
}: StudentCardProps) => {
  const { navToRoute } = useProtectedNavigation();
  const { id, majors, imageUrl, studentOrganizationStatus } = student;
  const { location, name, funnelState } = studentOrganizationStatus || {};

  return (
    <CarouselItem
      key={id}
      className="basis-auto pl-32px"
      data-testid={`student-card-${idx}`}
      aria-labelledby={`student-name-${idx}`}
    >
      <div className="relative flex flex-col gap-1">
        <StudentPopover student={student}>
          <div
            className="relative size-[160px] cursor-pointer"
            onClick={() =>
              id &&
              navToRoute("student", { studentId: id }, {}, { newTab: true })
            }
          >
            <Avatar shape="square" size="size-[160px]" className="rounded-md">
              <AvatarImage
                src={imageUrl?.url || undefined}
                alt={`${name} profile`}
                style={{ imageRendering: "crisp-edges" }}
              />
              <AvatarFallback className="bg-gray-200 text-gray-500">
                {(name || "U").charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div className="absolute bottom-2 left-2">
              {funnelState &&
                FunnelStateEnumToGroup["STUDENT_PROFILE"][funnelState]?.map(
                  (group) => {
                    return (
                      <Badge
                        key={group}
                        variant="translucent"
                        className="rounded-full bg-white/80 px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground backdrop-blur-lg"
                        data-testid="student-funnel-state-badge"
                      >
                        {translateGroupName(group)}
                      </Badge>
                    );
                  },
                )}
            </div>
          </div>
        </StudentPopover>

        <div className="flex max-w-[160px] flex-col gap-1">
          <h3
            id={`student-name-${idx}`}
            className="h-[25px] truncate text-sm/8 font-bold text-foreground"
          >
            {name}
          </h3>
          {showLocation ? (
            <WithLocationStudentCard location={String(location)} />
          ) : (
            <WithMajorStudentCard
              majors={majors?.map((major) => major.name ?? "") || []}
            />
          )}
        </div>
      </div>
    </CarouselItem>
  );
};
